import { Box } from '@mui/material'
import { SxProps } from '@mui/material'
import { ComponentType } from 'react'
import { capitalizeWords, hexToRgba, replaceCharacterWithSpace } from '@/utils/common'
import {
  TWO_FA_STATUS,
  VERIFICATION_STATUS,
  BANK_CONNECTION_STATUS,
  BANK_INSTITUTION_STATUS,
  LOAN_APPLICATION_STATUS,
  STATUS,
  RULES_STATUS,
  LOAN_STATUS,
  LOAN_SUBSTATUS,
  LENDING_PLATFORM,
} from '@/utils/constants'
import './style.css'

type ChipProps = {
  value: string
  subValue?: string
  variant?: string
  chipColor?: string
  icon?: ComponentType<any>
  sx?: SxProps
}

const defaultColor = '#344054'
const defaultErrorColor = '#c9272b'

const twoFaStatusMap = {
  [TWO_FA_STATUS.BLOCKED_FROM_2FA]: '#B42318',
  [TWO_FA_STATUS.UNBLOCKED]: '#067647',
}

const connectionMap = {
  [BANK_CONNECTION_STATUS.ACTIVE]: '#067647',
  // Amber: still live, but about to lapse. The label reads the same however it is spelled.
  [BANK_CONNECTION_STATUS.NEAR_EXPIRY]: '#B54708',
  near_expiry: '#B54708',
  [BANK_CONNECTION_STATUS.REVOKED]: '#B42318',
  [BANK_CONNECTION_STATUS.OPERATIONAL]: '#067647',
  [BANK_CONNECTION_STATUS.IN_PROGRESS]: '#344054',
  [BANK_CONNECTION_STATUS.PRE_INIT]: '#344054',
  [BANK_CONNECTION_STATUS.DEGRADED_PERFORMANCE]: '#344054',
  [BANK_CONNECTION_STATUS.PARTIAL_OUTAGE]: '#B93815',
  [BANK_CONNECTION_STATUS.UNDER_MAINTENANCE]: '#B42318',
  [BANK_CONNECTION_STATUS.MAJOR_OUTAGE]: '#B42318',
}

const institutionMap = {
  [BANK_INSTITUTION_STATUS.AVAILABLE]: '#067647',
  [BANK_INSTITUTION_STATUS.DEGRADED]: '#B93815',
  [BANK_INSTITUTION_STATUS.DOWN]: '#B42318',
  // Neither of these is an outage: the institution is simply not offered, or
  // the service sent no status. Neutral rather than alarming.
  [BANK_INSTITUTION_STATUS.UNSUPPORTED]: '#344054',
  [BANK_INSTITUTION_STATUS.UNSPECIFIED]: '#344054',
}

const verificationStatusMap = {
  [VERIFICATION_STATUS.VERIFIED]: '#067647',
  [VERIFICATION_STATUS.VERIFIED_ENROLLED_MFA]: '#067647',
  [VERIFICATION_STATUS.NOT_VERIFIED]: '#B42318',
  [VERIFICATION_STATUS.FAILED]: '#B42318',
  [VERIFICATION_STATUS.NOT_VERIFIED_ENROLLED_MFA]: '#B42318',
}

const statusMap = {
  [STATUS.SUCCESS]: '#067647',
  [STATUS.PENDING]: '#344054',
  [STATUS.FAILED]: '#B42318',
  [STATUS.INVALID]: '#B42318',
}

const loanApplicationMap = {
  [LOAN_APPLICATION_STATUS.APPROVED]: '#067647',
  [LOAN_APPLICATION_STATUS.DISBURSED]: '#067647',
  [LOAN_APPLICATION_STATUS.PENDING_AGREEMENT]: '#344054',
  [LOAN_APPLICATION_STATUS.PENDING_DISBURSEMENT]: '#344054',
  [LOAN_APPLICATION_STATUS.IN_REVIEW]: '#344054',
  [LOAN_APPLICATION_STATUS.SUBMITTED]: '#344054',
  [LOAN_APPLICATION_STATUS.DRAFT]: '#344054',
  [LOAN_APPLICATION_STATUS.REFERRED]: '#B54708',
  [LOAN_APPLICATION_STATUS.CANCELED]: '#B42318',
  [LOAN_APPLICATION_STATUS.FAILED_DISBURSEMENT]: '#B42318',
  [LOAN_APPLICATION_STATUS.DECLINED]: '#B42318',
  [LOAN_APPLICATION_STATUS.EXPIRED]: '#B42318',
  [LOAN_APPLICATION_STATUS.EXPIRED_AGREEMENT]: '#B42318',
} as const

const rulesMap = {
  [RULES_STATUS.PASS]: '#3fa06d',
  [RULES_STATUS.FAIL]: '#c9272b',
  [RULES_STATUS.NOT_APPLICABLE]: '#344054',
}

const loanProStatusMap = {
  [LOAN_STATUS.OPEN]: '#00c4a4',
  [LOAN_STATUS.CLOSED]: '#deeaf1',
  [LOAN_STATUS.PAID_OFF]: '#5b9939',
  [LOAN_SUBSTATUS.OPEN_REPAYING]: '#00c4a4',
  [LOAN_SUBSTATUS.OPEN_FRAUD_PROCESS]: '#00c4a4',
  [LOAN_SUBSTATUS.OPEN_DECEASED_PROCESS]: '#00c4a4',
  [LOAN_SUBSTATUS.OPEN_CHARGED_OFF_COLLECTIBLE]: '#00c4a4',
  [LOAN_SUBSTATUS.OPEN_PENDING_PAYOFF]: '#00c4a4',
  [LOAN_SUBSTATUS.OPEN_BANKRUPTCY]: '#00c4a4',
  [LOAN_SUBSTATUS.CLOSED_CHARGED_OFF]: '#deeaf1',
  [LOAN_SUBSTATUS.CLOSED_BANKRUPTCY]: '#deeaf1',
  [LOAN_SUBSTATUS.PAID_IN_FULL]: '#69a04b',
}

const hexMap = {
  ...twoFaStatusMap,
  ...verificationStatusMap,
  ...connectionMap,
  ...institutionMap,
  ...statusMap,
  ...loanApplicationMap,
  ...rulesMap,
  ...loanProStatusMap,
}

export type StatusType = keyof typeof hexMap

export const Chip = ({ value, chipColor, icon: IconComponent, sx }: ChipProps) => {
  const mapColorToHex = (value: string | null) => {
    if (!value) return
    const lowercaseVal = value.toLowerCase() as StatusType
    const colorHex = lowercaseVal[0] === 'e' ? defaultErrorColor : hexMap[lowercaseVal as StatusType] || defaultColor
    return {
      backgroundColor: chipColor ? hexToRgba(chipColor, 0.1) : hexToRgba(colorHex, 0.1),
      borderColor: chipColor ? hexToRgba(chipColor, 0.1) : hexToRgba(colorHex, 0.1),
      color: chipColor ?? colorHex,
    }
  }

  const colorProps = mapColorToHex(value)

  return (
    <Box
      sx={{
        width: 'fit-content',
        backgroundColor: colorProps?.backgroundColor,
        border: `1px solid ${colorProps?.borderColor}`,
        borderRadius: '9999px',
        padding: IconComponent ? '0 6px 0 0' : '0 6px',
        ...sx,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="center">
        {IconComponent && <IconComponent sx={{ fontSize: 20 }} />}
        <span className={'chip-text'} style={{ color: colorProps?.color }}>
          {value !== LENDING_PLATFORM
            ? capitalizeWords(replaceCharacterWithSpace(value, '_', '-'))
            : value}
        </span>
      </Box>
    </Box>
  )
}
